<template>
  <StepperTemplate :returnRoute="{}" :narrowBody="true" :showSteps="false">
    <template v-slot:title>
      <p
        class="h2-stepper-title text-center font-weight-bold text-blue-500 mb-3 mt-3 mt-md-0"
        v-orphans
        v-html="$t('form.installmentsPayment.header')"
      ></p>
    </template>
    <template v-slot:body>
      <div v-if="loader === false" class="px-lg-5 stepper-body-card">
        <p
          class="text-lg text-center text-blue-500"
          v-html="$t('form.installmentsPayment.title')"
        ></p>
        <div v-if="loader === false" class="position-relative">
          <b-row
            v-for="([key, item], index) in fields"
            :key="index"
            class="text-blue-500 text-base flex-md-nowrap px-md-5"
          >
            <b-col cols="12" md="auto">
              <p class="mb-0 mb-md-3 font-weight-bold d-block data-key-size">
                {{ $t(`form.installmentsPayment.${key}`) }}
              </p>
            </b-col>
            <b-col cols="12" md="10" lg="auto" class="pl-md-0 flex-grow-1">
              <div>
                <p v-if="key === 'paidTotal' || key === 'amount'">
                  {{
                    $t('form.installmentsPayment.currency', {
                      amount: convertToMajorUnit(item)
                    })
                  }}
                </p>
                <p v-else class="d-inline-flex">{{ item }}</p>
              </div>
            </b-col>
          </b-row>
          <div class="d-none d-md-block position-absolute avatar-position">
            <WChildAvatar v-bind="avatarParams" borderColor="var(--bs-mint-100)" />
          </div>
        </div>
      </div>
    </template>
    <template v-slot:bottomSection>
      <div class="container-xl mb-3">
        <b-row align-h="center">
          <b-col cols="12" md="8">
            <b-row no-gutters align-h="end">
              <b-col cols="12" md="auto">
                <b-button
                  id="button-initiate-checkout-swd"
                  @click="send"
                  block
                  variant="salmon-dark"
                  :disabled="loader || sendLoader"
                  class="btn text-base-mobile-lg font-weight-bold py-2 line-height-lg angle-right-icon-hover"
                >
                  {{ $t('form.installmentsPayment.send') }}
                  <i class="angle-right-icon ml-1 bg-white"></i>
                  <b-spinner v-if="sendLoader" small class="ml-2"></b-spinner>
                </b-button>
              </b-col>
              <b-col cols="12" class="mb-3"></b-col>
              <b-col cols="auto">
                <div class="d-flex align-items-end">
                  {{ $t('form.installmentsPayment.paymentProvider') }}
                  <b-img :src="p24Image" alt="Przelewy24" height="35" class="ml-2" />
                </div>
              </b-col>
            </b-row>
          </b-col>
        </b-row>
      </div>
    </template>
  </StepperTemplate>
</template>

<script>
import StepperTemplate from '@/components/widgets/StepperTemplate.vue'
import { PaymentType } from '@/types'

import useTransactionDataStore from '@/stores/transactionData'
import useDataLayerStore from '@/stores/dataLayer'
import { storeToRefs, mapActions } from 'pinia'

import redirectOnError from '@/router/helpers/redirectOnError'
import { useSummaryDataStore } from '@/stores/summary'
import { convertToMajorUnit } from '@/helpers/money'

export default {
  name: 'InstallmentPayment',
  components: { StepperTemplate },
  props: {
    hash: {
      required: true
    }
  },
  data() {
    return {
      loader: false,
      sendLoader: false,
      data: {
        paymentRecipient: null,
        giftGiverName: null,
        amount: null,
        installments: null,
        paidTotal: null,
        childName: null,
        avatarId: null,
        genderId: null,
        p24SessionId: null,
        installmentsId: null
      },
      p24Image: new URL('/src/assets/przelewy24_logo.svg', import.meta.url).href
    }
  },
  computed: {
    fields() {
      const HIDE_FIELDS_ARRAY = [
        'avatarId',
        'genderId',
        'paymentId',
        'p24SessionId',
        'installmentsId'
      ]
      return Object.entries(this.data).filter(([key]) => !HIDE_FIELDS_ARRAY.includes(key))
    },
    avatarParams() {
      return { avatarId: this.data.avatarId, genderId: this.data.genderId }
    }
  },
  methods: {
    convertToMajorUnit,
    ...mapActions(useDataLayerStore, ['saveDataForDataLayer']),
    send() {
      this.sendLoader = true
      let redirectUrl = null
      this.$store
        .dispatch('payment/postInstallmentsPaymentData', this.hash)
        .then(({ data: response }) => {
          // set data in storage for summary page
          this.paymentType = PaymentType.instalments
          this.amount = convertToMajorUnit(this.data?.amount)

          this.summaryDataStore.saveSummaryData({
            sessionId: this.data?.p24SessionId,
            fundraisingPayment: 0,
            childEditionId: this.data?.childEditionId
          })

          this.saveDataForDataLayer(this.data?.paymentId, this.data?.installmentsId)
          redirectUrl = response.data.p24Url
        })
        .finally(() => {
          if (redirectUrl) {
            window.location.assign(redirectUrl)
          }
        })
    }
  },
  created() {
    this.loader = true
    this.$store
      .dispatch('payment/getInstallmentsPaymentData', this.hash)
      .then(({ data }) => {
        this.data = data.data
      })
      .catch((error) => redirectOnError(this.$router, error))
      .finally(() => {
        this.loader = false
      })
  },
  setup() {
    const transactionDataStore = useTransactionDataStore()
    const { amount, paymentType } = storeToRefs(transactionDataStore)

    const summaryDataStore = useSummaryDataStore()

    return {
      amount,
      paymentType,
      summaryDataStore
    }
  },
  beforeUnmount() {
    this.sendLoader = false
  }
}
</script>

<style lang="scss" scoped>
.avatar-position {
  right: 0;
  bottom: 0;
}

.data-key-size {
  width: 100%;
  min-width: 100px;
}
</style>
